<template>
  <div class="gam-static-page" :class="{ mobile: isMobile }">
    <div class="page-header">
      <div class="page-header-container">
        <div class="page-title">
          {{ getTitle() }}
        </div>
      </div>
    </div>

    <ScrollOverflow class="static-scroll" is-vertical>
      <div class="static-body">
        <GamTab v-if="languageTabs" :tabs="languageTabs" :size="GamTabSize.MEDIUM" @update="onLanguageClick" />
        <div v-if="htmlContent" :class="['static-html', `lang-${activeLanguage}`]" v-html="htmlContent" />
      </div>
    </ScrollOverflow>

    <div class="static-close-btn" @click="handleClose">
      <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
    </div>
  </div>
</template>

<script setup lang="ts">
import { StaticPageName, TabItemType } from '@/core/data/config/uiConfig.interface';
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import GamIcon from '@/views/components/GamIcon.vue';
import GamTab from '@/views/components/gam-tab/GamTab.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamTabSize } from '@/views/composables/constants/components/gamTab.constants';
import type { GamTabItemType } from '@/views/composables/models/components/GamTab';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ScrollOverflow from './components/ScrollOverflow.vue';

const router = useRouter();
const route = useRoute();
const baseUrl = 'static/pages/';

const htmlContent = ref<string | null>(null);
const activeLanguage = ref<TabItemType>(TabItemType.ENGLISH);

const isBilingual = computed(
  () => !!htmlContent.value?.includes('data-lang="en"') && htmlContent.value.includes('data-lang="de"'),
);

const languageTabs = computed<GamTabItemType[] | null>(() => {
  if (!isBilingual.value) return null;

  return [
    {
      name: TabItemType.ENGLISH,
      displayName: 'static.pages.language.english',
      position: 0,
      isActive: activeLanguage.value === TabItemType.ENGLISH,
    },
    {
      name: TabItemType.GERMAN,
      displayName: 'static.pages.language.german',
      position: 1,
      isActive: activeLanguage.value === TabItemType.GERMAN,
    },
  ];
});

const loadStaticPage = async () => {
  const routeName = route.name?.toString();
  if (!Object.values(StaticPageName).includes(routeName as StaticPageName)) return;

  activeLanguage.value = TabItemType.ENGLISH;
  htmlContent.value = await gambits.staticService.loadHtmlContent(`${baseUrl}${routeName}.html`);
};

watch(() => route.name, loadStaticPage, { immediate: true });

const onLanguageClick = (name: TabItemType) => {
  activeLanguage.value = name;
};

const getTitle = () => {
  const routeName = route.name?.toString();
  return localize(`static.pages.${routeName}.title`);
};

const handleClose = () => {
  router.back();
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.gam-static-page {
  display: flex;
  height: 100vh;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.page-header {
  display: flex;
  justify-content: center;
  align-items: center;

  position: absolute;
  width: 100%;
  height: 7rem;
  background: linear-gradient(to bottom, var(--color-dark-700), transparent);
}

.page-header-container {
  width: 100%;
  max-width: 960px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 var(--spacing-xl);
}

.static-wrapper {
  display: flex;
  width: 100%;
  max-width: 600px;
  height: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-menu);
  position: relative;
}

.static-scroll {
  width: 100%;
}

.static-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: calc(2.5 * var(--spacing-xxl)) var(--spacing-xl) 8rem;
}

.static-html {
  width: 100%;
}

.page-title {
  flex: 1 0 0;
  font-size: 16px;
  font-weight: 700;
}

.static-close-btn {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

.frame-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-self: stretch;
}
</style>

<style lang="scss">
@use '@/ui/css/mixins' as *;
.gam-static-page {
  .content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
  }

  .lang-version {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-self: stretch;
  }

  .static-html.lang-en [data-lang='de'],
  .static-html.lang-de [data-lang='en'] {
    display: none;
  }

  .section-updated {
    display: flex;
    padding: 8px 16px 8px 8px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: 28px;
    background: var(--color-linear-gradient-30);
    margin-bottom: var(--spacing-xl);
  }

  .updated-time {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    overflow: hidden;
    color: #fff;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 400;
    line-height: 100%; /* 14px */
  }

  .section,
  .sub-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .title {
    font-weight: 600;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .content span {
    color: var(--color-white-70);
    font-size: 14px;
    line-height: 1.33;
    font-weight: 400;
  }

  .content ul {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding-left: var(--spacing-xl);
  }

  .content li {
    color: var(--color-white-70);
    font-size: 14px;
    line-height: 1.33;
    font-weight: 400;
  }

  .table-scroll {
    align-self: stretch;
    overflow-x: auto;
  }

  .content table {
    width: 100%;
    min-width: 560px;
    border-collapse: collapse;
    color: var(--color-white-70);
    font-size: 14px;
    line-height: 1.33;
  }

  .content th,
  .content td {
    padding: var(--spacing-s) var(--spacing-md);
    border-bottom: 1px solid var(--color-white-10);
    text-align: left;
    vertical-align: top;
  }

  .content th {
    color: var(--color-white);
    font-weight: 600;
  }

  .static-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-l) var(--spacing-xxl);
    border-radius: var(--radius-large);
    background: var(--color-linear-gradient-30), rgba(0, 0, 0, 1);
    color: var(--color-white);
    font-size: 16px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: background 200ms linear;

    @include hover {
      background: var(--color-linear-gradient-30), rgba(255, 255, 255, 0.25);
    }

    @include press;
  }

  .section .title {
    font-size: 24px;
    line-height: 1;
    letter-spacing: 0.02em;
  }

  .sub-section .title {
    font-size: 18px;
    line-height: 1;
  }

  .sub-section .content {
    font-weight: 400;
  }
}
</style>
